import React from "react";
import { Link } from "react-router-dom";
import ArticleLayout, { H2, DataTable } from "./ArticleLayout";

export default function ArticleRetireAt65() {
  return (
    <ArticleLayout
      meta={{
        slug: "how-much-to-retire-at-65",
        hub: "Retirement Readiness",
        time: "8 min read",
        title: "How Much Money Do I Need to Retire at 65? | Centennial",
        seoDescription:
          "A practical target: 25–30x your annual spending gap after Social Security. Worked examples for $40k, $60k and $80k lifestyles, and a free calculator to test your real number.",
        h1: "How Much Money Do I Need to Retire at 65?",
        hook: "Not a myth, not a scare number — a formula you can actually compute for your own life in about five minutes.",
        quickAnswer:
          "Multiply your spending gap by 25 to 30. Take your expected annual spending, subtract Social Security and any pension, and multiply what's left by 25 (aggressive, 4% withdrawals) to 30 (conservative, ~3.3% — better for planning to age 100). Example: $60,000 spending − $28,000 Social Security = $32,000 gap × 25–30 = $800,000 to $960,000 needed at 65.",
      }}
    >
      <H2>The formula</H2>
      <p>
        Savings needed = (annual spending − guaranteed income) × 25 to 30. The multiplier is just
        the inverse of your withdrawal rate: 25× equals 4% withdrawals, 30× equals 3.3%. For
        retirements that might run 35 years — the kind Centennial models to age 100 — lean
        toward the higher multiple (see{" "}
        <Link className="text-[#2B5339] underline" to="/learn/four-percent">why the 4% rule struggles on long horizons</Link>).
      </p>

      <H2>Worked examples</H2>
      <DataTable
        head={["Annual spending", "Social Security (couple)", "Gap", "Needed at 65 (25–30×)"]}
        rows={[
          ["$40,000", "$28,000", "$12,000", "$300k – $360k"],
          ["$60,000", "$28,000", "$32,000", "$800k – $960k"],
          ["$80,000", "$36,000", "$44,000", "$1.1M – $1.32M"],
          ["$100,000", "$36,000", "$64,000", "$1.6M – $1.92M"],
        ]}
      />
      <p>
        Notice what drives the answer: it's the <em>gap</em>, not the gross spending. Two
        households spending $60,000 can need wildly different nest eggs depending on Social
        Security timing (<Link className="text-[#2B5339] underline" to="/learn/social-security-claiming-age">62 vs 67 vs 70 comparison</Link>)
        and whether a pension exists.
      </p>

      <H2>Estimate your spending honestly</H2>
      <p>
        Start from today's take-home spending, then adjust: subtract the mortgage if it will be
        paid off, subtract retirement contributions, add travel and hobbies for the early years,
        and add healthcare as its own inflating line —{" "}
        <Link className="text-[#2B5339] underline" to="/learn/retirement-healthcare-costs">what healthcare really costs in retirement</Link>.
        Most retirees land between 70% and 90% of pre-retirement spending.
      </p>

      <H2>Why a multiplier isn't enough</H2>
      <p>
        The 25–30× rule assumes average markets in an average order. Real retirements meet real
        sequences — retiring into a 2008 changes everything even when long-run averages hold
        (<Link className="text-[#2B5339] underline" to="/learn/sequence-risk">sequence-of-returns risk explained</Link>).
        That's why the next step after the formula is a simulation: run your exact numbers
        through 5,000 market paths and the worst historical sequences, free, in the{" "}
        <Link className="text-[#2B5339] underline" to="/">Centennial calculator</Link>.
      </p>

      <H2>Beyond the number</H2>
      <p>
        Readiness is more than a balance. Estate documents, insurance, housing, family
        conversations — score yourself on all of it with the free{" "}
        <Link className="text-[#2B5339] underline" to="/learn/readiness-checklist">50-question readiness checklist</Link>{" "}
        and the deeper <Link className="text-[#2B5339] underline" to="/learn/readiness-workbook">readiness workbook</Link>.
      </p>
    </ArticleLayout>
  );
}
